import { byId } from './domUtils';
import { getRuntime } from './runtimeAccessor';
import { cancelTurnstilePrefetch, refreshTurnstileProviders } from './turnstileRuntime';
import type { InteractiveTurnstileProvider } from './turnstileClient';
import type { PrototypeRuntime, Root } from './prototypeTypes';
import { paintOperationFeedback } from './operationFeedback';
import { clearTurnstileWidgetSurface, type TurnstileWidgetCallbacks } from './turnstileWidgetRenderer';

export type TurnstileSurfaceOwner = 'modal' | 'wizard-step';

export interface TurnstileSurfaceDefinition {
	readonly id: string;
	readonly owner: TurnstileSurfaceOwner;
	readonly modalId: string;
	readonly containerId: string;
	readonly buttonId: string;
	readonly action: string;
}

export const TURNSTILE_SURFACE_INVENTORY = [
	{ id: 'login', owner: 'modal', modalId: 'loginModal', containerId: 'loginTurnstileContainer', buttonId: 'loginSubmitBtn', action: 'auth-login' },
	{
		id: 'register',
		owner: 'modal',
		modalId: 'registerModal',
		containerId: 'registerTurnstileContainer',
		buttonId: 'registerSubmitBtn',
		action: 'auth-register',
	},
	{
		id: 'resend-verification',
		owner: 'modal',
		modalId: 'registerModal',
		containerId: 'resendVerificationTurnstileContainer',
		buttonId: 'resendVerificationSubmitBtn',
		action: 'auth-resend-verification',
	},
	{
		id: 'forgot-password',
		owner: 'modal',
		modalId: 'forgotModal',
		containerId: 'forgotTurnstileContainer',
		buttonId: 'forgotSubmitBtn',
		action: 'auth-password-reset',
	},
	{
		id: 'oauth-link',
		owner: 'modal',
		modalId: 'oauthLinkModal',
		containerId: 'oauthLinkTurnstileContainer',
		buttonId: 'oauthLinkSubmitBtn',
		action: 'auth-google-link',
	},
	{
		id: 'set-password',
		owner: 'modal',
		modalId: 'setPasswordConfirmModal',
		containerId: 'setPasswordTurnstileContainer',
		buttonId: 'setPasswordConfirmBtn',
		action: 'auth-set-password',
	},
	{
		id: 'cancel-auto-renew',
		owner: 'modal',
		modalId: 'cancelAutoRenewModal',
		containerId: 'cancelAutoRenewTurnstileContainer',
		buttonId: 'confirmCancelAutoRenewBtn',
		action: 'billing-auto-renew-cancel',
	},
	{ id: 'ai-create', owner: 'wizard-step', modalId: 'aiCreateModal', containerId: 'aiTurnstileContainer', buttonId: 'aiSubmitBtn', action: 'ai-create' },
	{
		id: 'submit-idea',
		owner: 'wizard-step',
		modalId: 'submitIdeaModal',
		containerId: 'ideaTurnstileContainer',
		buttonId: 'ideaSubmitBtn',
		action: 'community-idea-submit',
	},
] as const satisfies readonly TurnstileSurfaceDefinition[];

export type ModalTurnstileSurface = Extract<(typeof TURNSTILE_SURFACE_INVENTORY)[number], { owner: 'modal' }>;
export type WizardTurnstileSurface = Extract<(typeof TURNSTILE_SURFACE_INVENTORY)[number], { owner: 'wizard-step' }>;
type TurnstileSurface = (typeof TURNSTILE_SURFACE_INVENTORY)[number];

const modalSurfaces = TURNSTILE_SURFACE_INVENTORY.filter((surface): surface is ModalTurnstileSurface => surface.owner === 'modal');
const wizardSurfaces = TURNSTILE_SURFACE_INVENTORY.filter((surface): surface is WizardTurnstileSurface => surface.owner === 'wizard-step');
const registeredModalIds = new Set<string>(modalSurfaces.map((surface) => surface.modalId));
const COMMUNITY_VOTE_TURNSTILE_ACTION = 'community-idea-vote';

interface RuntimeLease {
	readonly generation: number;
	readonly modalId: string;
	readonly surface: TurnstileSurface;
	phase: 'mounting' | 'mounted' | 'failed';
	provider?: InteractiveTurnstileProvider;
	callbacks?: TurnstileWidgetCallbacks;
}

interface RuntimeLeaseState {
	nextGeneration: number;
	current: RuntimeLease | null;
}

const runtimeLeases = new WeakMap<PrototypeRuntime, RuntimeLeaseState>();

function leaseState(runtime: PrototypeRuntime): RuntimeLeaseState {
	let state = runtimeLeases.get(runtime);
	if (!state) {
		state = { nextGeneration: 0, current: null };
		runtimeLeases.set(runtime, state);
	}
	return state;
}

function ownsLease(runtime: PrototypeRuntime, lease: RuntimeLease): boolean {
	return leaseState(runtime).current === lease;
}

function isVisible(element: HTMLElement | null): boolean {
	return Boolean(element && !element.hidden && element.style.display !== 'none');
}

export function resolveModalTurnstileSurface(root: Root, modalId: string): ModalTurnstileSurface | null {
	if (!registeredModalIds.has(modalId)) return null;
	if (modalId === 'registerModal') {
		const pending = byId(root, 'registerSuccess');
		if (isVisible(pending) && byId(root, 'resendVerificationControls')?.hidden) return null;
		const surfaceId = isVisible(pending) ? 'resend-verification' : 'register';
		return modalSurfaces.find((surface) => surface.id === surfaceId) ?? null;
	}
	return modalSurfaces.find((surface) => surface.modalId === modalId) ?? null;
}

export async function reconcileModalTurnstile(root: Root, modalId: string, runtimeOverride?: PrototypeRuntime): Promise<void> {
	const surface = resolveModalTurnstileSurface(root, modalId);
	const runtime = runtimeOverride ?? getRuntime(root);
	if (!surface) {
		if (registeredModalIds.has(modalId)) disposeModalTurnstile(root, modalId, runtime);
		return;
	}
	if (!runtime) return;
	await mountTurnstileSurface(root, runtime, surface);
}

export async function reconcileWizardTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	surfaceId: WizardTurnstileSurface['id'],
	callbacks?: TurnstileWidgetCallbacks,
): Promise<void> {
	const surface = wizardSurfaces.find((candidate) => candidate.id === surfaceId);
	if (!surface) return;
	await mountTurnstileSurface(root, runtime, surface, callbacks);
}

async function mountTurnstileSurface(
	root: Root,
	runtime: PrototypeRuntime,
	surface: TurnstileSurface,
	callbacks?: TurnstileWidgetCallbacks,
): Promise<void> {
	const state = leaseState(runtime);
	if (
		state.current?.surface.id === surface.id
		&& (state.current.phase === 'mounting' || (state.current.phase === 'mounted' && state.current.provider === runtime.interactiveTurnstile))
	) return;
	if (!byId(root, surface.containerId) || !byId(root, surface.buttonId)) return;
	// A protected modal/wizard is foreground work. Stop the Community page's
	// best-effort warm-up immediately, including while public config is still
	// loading, so it cannot surface ahead of this visible widget.
	cancelTurnstilePrefetch(runtime, COMMUNITY_VOTE_TURNSTILE_ACTION);

	const lease: RuntimeLease = {
		generation: ++state.nextGeneration,
		modalId: surface.modalId,
		surface,
		phase: 'mounting',
		callbacks,
	};
	const previous = state.current;
	state.current = lease;
	if (previous && previous.surface.id !== surface.id) {
		clearSurface(root, previous.surface);
		paintSurfaceStatus(root, previous.surface, null);
	}
	clearSurface(root, surface);
	paintSurfaceStatus(root, surface, null);

	let provider = runtime.interactiveTurnstile;
	if (!provider) {
		try {
			await refreshTurnstileProviders(runtime);
		} catch {
			// The surface remains fail-closed and can be reconciled again by recovery.
		}
		if (!ownsLease(runtime, lease)) return;
		provider = runtime.interactiveTurnstile;
	}
	if (!provider || !ownsLease(runtime, lease)) {
		if (ownsLease(runtime, lease)) {
			lease.phase = 'failed';
			paintSurfaceError(root, surface);
		}
		return;
	}
	lease.provider = provider;
	const container = byId(root, surface.containerId);
	if (!container) return;
	try {
		await provider.renderWidget(container, surface.action, {
			onSuccess: (token) => {
				if (!ownsLease(runtime, lease)) return;
				const button = byId(root, surface.buttonId) as HTMLButtonElement | null;
				if (button?.dataset.submitting === 'true') return;
				paintSurfaceStatus(root, surface, null);
				if (surface.id === 'cancel-auto-renew') container.dataset.turnstileToken = token;
				unlockSurface(root, surface);
				callbacks?.onSuccess(token);
			},
			onError: () => {
				if (!ownsLease(runtime, lease)) return;
				lease.phase = 'failed';
				lockSurface(root, surface);
				paintSurfaceError(root, surface);
				callbacks?.onError();
			},
			onExpired: () => {
				if (!ownsLease(runtime, lease)) return;
				lockSurface(root, surface);
				paintSurfaceExpired(root, surface);
				provider?.resetWidget();
				callbacks?.onExpired();
			},
		});
		if (ownsLease(runtime, lease) && lease.phase === 'mounting') lease.phase = 'mounted';
	} catch {
		if (ownsLease(runtime, lease)) {
			lease.phase = 'failed';
			lockSurface(root, surface);
			paintSurfaceError(root, surface);
			if (surface.id === 'cancel-auto-renew') provider.resetWidget();
			callbacks?.onError();
		}
	}
}

export function disposeModalTurnstile(root: Root, modalId: string, runtimeOverride?: PrototypeRuntime): void {
	if (!registeredModalIds.has(modalId)) return;
	for (const surface of modalSurfaces) {
		if (surface.modalId === modalId) {
			clearSurface(root, surface);
			paintSurfaceStatus(root, surface, null);
		}
	}

	const runtime = runtimeOverride ?? getRuntime(root);
	if (!runtime) return;
	const state = leaseState(runtime);
	const lease = state.current;
	if (!lease || lease.modalId !== modalId) return;
	state.nextGeneration += 1;
	state.current = null;
	lease.provider?.removeWidget();
}

export async function restartModalTurnstile(root: Root, modalId: string, runtimeOverride?: PrototypeRuntime): Promise<void> {
	disposeModalTurnstile(root, modalId, runtimeOverride);
	if (!byId(root, modalId)?.classList.contains('active')) return;
	await reconcileModalTurnstile(root, modalId, runtimeOverride);
}

export async function reconcileActiveModalTurnstile(root: Root, runtimeOverride?: PrototypeRuntime): Promise<void> {
	const active = Array.from(root.querySelectorAll<HTMLElement>('.modal-overlay.active'))
		.reverse()
		.find((modal) => registeredModalIds.has(modal.id) && !modal.hidden);
	if (active) await reconcileModalTurnstile(root, active.id, runtimeOverride);
}

export function disposeWizardTurnstile(
	root: Root,
	surfaceId: WizardTurnstileSurface['id'],
	runtimeOverride?: PrototypeRuntime,
): void {
	const surface = wizardSurfaces.find((candidate) => candidate.id === surfaceId);
	if (!surface) return;
	clearSurface(root, surface);
	paintSurfaceStatus(root, surface, null);

	const runtime = runtimeOverride ?? getRuntime(root);
	if (!runtime) return;
	const state = leaseState(runtime);
	const lease = state.current;
	if (!lease || lease.surface.id !== surfaceId) return;
	state.nextGeneration += 1;
	state.current = null;
	lease.provider?.removeWidget();
}

export async function restartWizardTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	surfaceId: WizardTurnstileSurface['id'],
	callbacks?: TurnstileWidgetCallbacks,
): Promise<void> {
	disposeWizardTurnstile(root, surfaceId, runtime);
	const surface = wizardSurfaces.find((candidate) => candidate.id === surfaceId);
	if (!surface || !isActiveWizardSurface(root, surface)) return;
	await reconcileWizardTurnstile(root, runtime, surfaceId, callbacks);
}

export async function reconcileVisibleWizardTurnstile(root: Root, runtimeOverride?: PrototypeRuntime): Promise<void> {
	const hasActiveRegisteredModal = Array.from(root.querySelectorAll<HTMLElement>('.modal-overlay.active'))
		.some((modal) => registeredModalIds.has(modal.id) && !modal.hidden);
	if (hasActiveRegisteredModal) return;
	const surface = wizardSurfaces.find((candidate) => isActiveWizardSurface(root, candidate));
	const runtime = runtimeOverride ?? getRuntime(root);
	if (surface && runtime) await reconcileWizardTurnstile(root, runtime, surface.id);
}

function isActiveWizardSurface(root: Root, surface: WizardTurnstileSurface): boolean {
	const modal = byId(root, surface.modalId);
	const container = byId(root, surface.containerId);
	const panel = container?.closest<HTMLElement>('.wizard-panel');
	return Boolean(modal?.classList.contains('active') && !modal.hidden && panel?.classList.contains('active'));
}

function lockSurface(root: Root, surface: TurnstileSurface): void {
	const container = byId(root, surface.containerId);
	if (container) delete container.dataset.turnstileToken;
	const button = byId(root, surface.buttonId) as HTMLButtonElement | null;
	if (!button) return;
	button.disabled = true;
	button.setAttribute('aria-disabled', 'true');
	button.setAttribute('data-locked', '');
}

function clearSurface(root: Root, surface: TurnstileSurface): void {
	clearTurnstileWidgetSurface(root, surface.containerId, surface.buttonId);
	lockSurface(root, surface);
}

function unlockSurface(root: Root, surface: TurnstileSurface): void {
	const button = byId(root, surface.buttonId) as HTMLButtonElement | null;
	if (!button) return;
	button.disabled = false;
	button.setAttribute('aria-disabled', 'false');
	button.removeAttribute('data-locked');
}

const authStatusIds: Partial<Record<TurnstileSurface['id'], { statusId: string; textId: string }>> = {
	login: { statusId: 'loginTurnstileStatus', textId: 'loginTurnstileStatusText' },
	register: { statusId: 'registerTurnstileStatus', textId: 'registerTurnstileStatusText' },
	'resend-verification': { statusId: 'resendVerificationTurnstileStatus', textId: 'resendVerificationTurnstileStatusText' },
	'forgot-password': { statusId: 'forgotTurnstileStatus', textId: 'forgotTurnstileStatusText' },
	'oauth-link': { statusId: 'oauthLinkTurnstileStatus', textId: 'oauthLinkTurnstileStatusText' },
	'set-password': { statusId: 'setPasswordTurnstileStatus', textId: 'setPasswordTurnstileStatusText' },
	'cancel-auto-renew': { statusId: 'cancelAutoRenewFeedback', textId: 'cancelAutoRenewFeedback' },
};

function paintSurfaceStatus(root: Root, surface: TurnstileSurface, message: string | null): void {
	const statusIds = authStatusIds[surface.id];
	if (!statusIds) return;
	const status = byId(root, statusIds.statusId);
	const text = byId(root, statusIds.textId);
	if (text) text.textContent = message ?? '';
	if (status) status.hidden = !message;
}

function paintSurfaceError(root: Root, surface: TurnstileSurface): void {
	if (surface.id === 'cancel-auto-renew') {
		paintOperationFeedback(root, 'cancelAutoRenewFeedback', 'Verification failed. Please try again.');
		return;
	}
	paintSurfaceStatus(root, surface, 'Verification could not be completed. Check your connection, then retry.');
}

function paintSurfaceExpired(root: Root, surface: TurnstileSurface): void {
	if (surface.id !== 'cancel-auto-renew') paintSurfaceStatus(root, surface, 'Verification expired. Please retry.');
}
